import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronDown, Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface NavSection {
  id: string;
  label: string;
  icon?: React.ReactNode;
}

interface OverlaySideNavProps {
  sections: NavSection[];
  activeSection?: string;
  onSectionClick?: (id: string) => void;
  className?: string;
}

export function OverlaySideNav({
  sections,
  activeSection: controlledActiveSection,
  onSectionClick,
  className,
}: OverlaySideNavProps) {
  const [internalActiveSection, setInternalActiveSection] = useState<string>(sections[0]?.id || '');
  const [isVisible, setIsVisible] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isHoveringRef = useRef(false);
  const lastScrollY = useRef(0);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeSection = controlledActiveSection ?? internalActiveSection;

  // Track active section based on scroll position (only if not controlled)
  useEffect(() => {
    if (controlledActiveSection !== undefined) return;

    const observerCallback: IntersectionObserverCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setInternalActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0,
    });

    sections.forEach(section => {
      const element = document.getElementById(section.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [sections, controlledActiveSection]);

  // Show icon on scroll (both directions), hide after idle — unless panel is open
  useEffect(() => {
    const handleScroll = () => {
      if (panelOpen) return;

      const currentScrollY = window.scrollY;

      if (Math.abs(currentScrollY - lastScrollY.current) > 5) {
        setIsVisible(true);

        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        scrollTimeoutRef.current = setTimeout(() => {
          if (!isHoveringRef.current) {
            setIsVisible(false);
          }
        }, 1500);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [panelOpen]);

  // Hover zone detection (100px from left edge) — only when panel is closed
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (panelOpen) return;

      const isInHoverZone = e.clientX <= 100;

      if (isInHoverZone && !isHoveringRef.current) {
        isHoveringRef.current = true;
        setIsVisible(true);
      } else if (!isInHoverZone && isHoveringRef.current) {
        isHoveringRef.current = false;
        if (scrollTimeoutRef.current === null) {
          setIsVisible(false);
        }
      }
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [panelOpen]);

  const scrollToSection = useCallback((id: string) => {
    if (onSectionClick) {
      onSectionClick(id);
    } else {
      const element = document.getElementById(id);
      if (element) {
        const yOffset = -100;
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
    setPanelOpen(false);
    setMobileOpen(false);
  }, [onSectionClick]);

  const openPanel = useCallback(() => {
    setPanelOpen(true);
    setIsVisible(false); // icon hides once panel takes over
  }, []);

  const closePanel = useCallback(() => {
    setPanelOpen(false);
    isHoveringRef.current = false;
  }, []);

  // Keep panel visible while hovering over it (doesn't re-trigger icon visibility logic)
  const handleNavMouseEnter = useCallback(() => {
    isHoveringRef.current = true;
  }, []);

  const handleNavMouseLeave = useCallback(() => {
    isHoveringRef.current = false;
  }, []);

  return (
    <>
      {/* Desktop: Floating hamburger icon (collapsed state) */}
      <AnimatePresence>
        {isVisible && !panelOpen && (
          <motion.button
            initial={{ x: -60, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -60, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            onClick={openPanel}
            className={cn(
              'hidden lg:flex fixed left-4 top-1/3 z-40',
              'items-center justify-center',
              'w-10 h-10 rounded-xl',
              'bg-white/95 backdrop-blur-sm shadow-lg border border-gray-200',
              'text-gray-500 hover:text-eventus-navy hover:border-eventus-purple/40 hover:shadow-xl',
              'transition-colors duration-200 cursor-pointer',
              className
            )}
            aria-label="Open page navigation"
          >
            <Menu className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Desktop: Expanded nav panel (open state) */}
      <AnimatePresence>
        {panelOpen && (
          <>
            {/* Backdrop — click outside to close */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              onClick={closePanel}
              className="hidden lg:block fixed inset-0 z-30"
              aria-hidden="true"
            />

            <motion.nav
              initial={{ x: -280, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -280, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              onMouseEnter={handleNavMouseEnter}
              onMouseLeave={handleNavMouseLeave}
              className={cn(
                'hidden lg:block fixed left-4 top-24 w-56 z-40',
                className
              )}
              aria-label="Page navigation"
            >
              <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200 p-4 max-h-[calc(100vh-8rem)] overflow-y-auto">
                {/* Header with close button */}
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    On this page
                  </h2>
                  <button
                    onClick={closePanel}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors"
                    aria-label="Close navigation"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>

                <ul className="space-y-1">
                  {sections.map((section) => {
                    const isActive = activeSection === section.id;
                    return (
                      <li key={section.id} className="relative">
                        <button
                          onClick={() => scrollToSection(section.id)}
                          className={cn(
                            'w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200',
                            'flex items-center gap-2',
                            isActive
                              ? 'bg-eventus-purple/10 text-eventus-purple font-medium'
                              : 'text-gray-600 hover:bg-gray-100 hover:text-eventus-navy'
                          )}
                        >
                          {section.icon && (
                            <span className={cn(
                              'flex-shrink-0',
                              isActive ? 'text-eventus-purple' : 'text-gray-400'
                            )}>
                              {section.icon}
                            </span>
                          )}
                          <span className="truncate">{section.label}</span>
                        </button>
                        {isActive && (
                          <motion.div
                            layoutId="overlay-nav-indicator"
                            className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-eventus-purple rounded-r-full"
                            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                          />
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>

      {/* Mobile: Floating "Jump to" button */}
      <div className="lg:hidden fixed bottom-20 right-4 z-50">
        <AnimatePresence>
          {!mobileOpen && (
            <motion.button
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              onClick={() => setMobileOpen(true)}
              className="flex items-center gap-2 px-4 py-3 bg-eventus-purple text-white rounded-full shadow-lg hover:bg-eventus-purple-dark transition-colors"
              aria-label="Open navigation"
            >
              <Menu className="w-4 h-4" />
              <span className="text-sm font-medium">Jump to</span>
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile: Full-screen section selector */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="lg:hidden fixed inset-0 bg-black/50 z-50"
              aria-hidden="true"
            />

            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="lg:hidden fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl z-50 max-h-[70vh] overflow-hidden"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-eventus-navy">
                  Jump to section
                </h2>
                <button
                  onClick={() => setMobileOpen(false)}
                  className="p-2 text-gray-500 hover:text-gray-700 rounded-lg"
                  aria-label="Close navigation"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="overflow-y-auto max-h-[calc(70vh-60px)] p-4">
                <ul className="space-y-2">
                  {sections.map((section) => {
                    const isActive = activeSection === section.id;
                    return (
                      <li key={section.id}>
                        <button
                          onClick={() => scrollToSection(section.id)}
                          className={cn(
                            'w-full text-left px-4 py-3 rounded-xl transition-all',
                            'flex items-center gap-3',
                            isActive
                              ? 'bg-eventus-purple text-white'
                              : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                          )}
                        >
                          {section.icon && (
                            <span className={cn(
                              'flex-shrink-0',
                              isActive ? 'text-white' : 'text-gray-400'
                            )}>
                              {section.icon}
                            </span>
                          )}
                          <span className="font-medium">{section.label}</span>
                          {isActive && (
                            <ChevronDown className="w-4 h-4 ml-auto" />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
